/**
 * OrderActionConfirm — confirmation popup for a forward order action.
 *
 * Two variants share one layout (see design):
 *   A · simple confirm  → status chips + explanatory body + Cancel/Primary
 *   B · input-collecting → status chips + "Estimated delivery (days)" input
 *                          (Mark shipped) + Cancel/Primary
 *
 * Purely presentational: it owns only the local ETA input and reports the
 * parsed value back through onConfirm. The caller runs the mutation. Used by
 * both the orders list quick actions and the order detail primary action.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, CloseIcon, NumberInput } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { OrderStatus } from "@/lib/api/types";
import {
  DISPLAY_STATUS_TONE,
  toDisplayStatus,
} from "@/modules/orders/lib/order-status-display";

type Props = {
  isOpen: boolean;
  /** Order code shown in the header. */
  orderCode: string;
  fromStatus: OrderStatus;
  toStatus: OrderStatus;
  /** Explanatory sentence key — shown when the action collects no input. */
  bodyKey: string | null;
  /** Pre-resolved body text — overrides bodyKey (for dynamic copy). */
  bodyText?: string;
  /** Primary button label key. */
  confirmKey?: string;
  /** Pre-resolved primary button label — overrides confirmKey. */
  confirmLabel?: string;
  /** When true, collects an optional estimated-delivery value instead of body. */
  needsEta: boolean;
  isSubmitting: boolean;
  /** Localized error message to surface inline, if any. */
  error?: string | null;
  /** Receives the parsed ETA (null when empty / not applicable). */
  onConfirm: (etaDays: number | null) => void;
  onClose: () => void;
};

export function OrderActionConfirm({
  isOpen,
  orderCode,
  fromStatus,
  toStatus,
  bodyKey,
  bodyText,
  confirmKey,
  confirmLabel,
  needsEta,
  isSubmitting,
  error,
  onConfirm,
  onClose,
}: Props) {
  const t = useTranslations();
  const [etaInput, setEtaInput] = useState("");
  const touchStartY = useRef<number | null>(null);

  // Reset the input on each open transition (render-time, no effect needed).
  const [prevOpen, setPrevOpen] = useState(isOpen);
  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    if (isOpen) setEtaInput("");
  }

  // Escape to close (when not mid-submit).
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSubmitting) {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen || typeof document === "undefined") return null;

  const requestClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleConfirm = () => {
    const trimmed = etaInput.trim();
    const eta = needsEta && trimmed ? Number(trimmed) : null;
    onConfirm(Number.isFinite(eta) ? eta : null);
  };

  // Portal to body: the order-detail modal panel uses a transform, which would
  // otherwise trap this `fixed` overlay inside the modal bounds.
  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/40"
        onClick={requestClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-[70] flex items-end md:items-center md:justify-center md:p-4">
        <div
          role="dialog"
          aria-modal="true"
          onTouchStart={(event) => {
            touchStartY.current = event.changedTouches[0]?.clientY ?? null;
          }}
          onTouchEnd={(event) => {
            const startY = touchStartY.current;
            const endY = event.changedTouches[0]?.clientY ?? null;
            if (startY !== null && endY !== null && endY - startY > 80) {
              requestClose();
            }
            touchStartY.current = null;
          }}
          className="flex w-full flex-col rounded-t-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-lg)] md:max-w-md md:rounded-2xl"
        >
          {/* Drag handle — mobile only */}
          <div
            className="flex justify-center pt-3 md:hidden"
            aria-hidden="true"
          >
            <div className="h-1 w-8 rounded-full bg-[color:var(--border-light)]" />
          </div>

          {/* Header — order code + close */}
          <div className="flex items-center justify-between px-5 pb-2 pt-4">
            <h3
              dir="ltr"
              className="truncate font-mono text-[15px] font-semibold text-[color:var(--text-primary)]"
            >
              {orderCode}
            </h3>
            <button
              type="button"
              onClick={requestClose}
              aria-label={t(keys.common.cancel)}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[color:var(--border-light)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)]"
            >
              <CloseIcon size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 pb-1">
            {/* Status transition chips */}
            <div className="flex items-center gap-2">
              <StatusChip status={fromStatus} t={t} />
              <ArrowIcon />
              <StatusChip status={toStatus} t={t} />
            </div>

            {needsEta ? (
              <div className="mt-4">
                <label
                  htmlFor="orderActionConfirmEta"
                  className="block text-[13px] font-semibold text-[color:var(--text-primary)]"
                >
                  {t(keys.orders.estimatedDeliveryLabel)}
                </label>
                <NumberInput
                  id="orderActionConfirmEta"
                  name="orderActionConfirmEta"
                  min={0}
                  value={etaInput}
                  onChange={(event) => setEtaInput(event.target.value)}
                  placeholder={t(keys.orders.estimatedDeliveryExample)}
                  className="mt-1.5 h-11 w-full rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-sm"
                />
                <p className="mt-1.5 text-[13px] text-[color:var(--text-muted)]">
                  {t(keys.orders.estimatedDeliveryHelperOptional)}
                </p>
              </div>
            ) : (bodyText ?? bodyKey) ? (
              <p className="mt-4 text-[13px] leading-relaxed text-[color:var(--text-secondary)]">
                {bodyText ?? (bodyKey ? t(bodyKey) : null)}
              </p>
            ) : null}

            {error ? (
              <div
                className="mt-3 rounded-lg border border-[color:var(--error-500)]/35 bg-[color:var(--error-500)]/10 px-3 py-2 text-[13px] text-[color:var(--error-500)]"
                role="alert"
              >
                {error}
              </div>
            ) : null}
          </div>

          {/* Footer actions */}
          <div className="flex items-center gap-2 px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4">
            <Button
              type="button"
              variant="outline"
              className="h-11 flex-1 rounded-lg text-sm font-semibold"
              onClick={requestClose}
              disabled={isSubmitting}
            >
              {t(keys.common.cancel)}
            </Button>
            <Button
              type="button"
              variant="primary"
              className="h-11 flex-1 rounded-lg text-sm font-semibold"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {confirmLabel ?? (confirmKey ? t(confirmKey) : "")}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function StatusChip({
  status,
  t,
}: {
  status: OrderStatus;
  t: ReturnType<typeof useTranslations>;
}) {
  const tone = DISPLAY_STATUS_TONE[toDisplayStatus(status)];
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-[13px] font-semibold"
      style={{ background: tone.bg, color: tone.fg }}
    >
      {t(tone.labelKey)}
    </span>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[color:var(--text-disabled)] rtl:rotate-180"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
